import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, MapPin, Navigation, User, Utensils } from 'lucide-react';

export const venueConcierge: Playbook = {
  meta: {
    id: 'venue-concierge',
    slug: 'venue-concierge',
    categoryId: 'physical-world',
    name: 'The Location Concierge',
    tagline: 'A local expert at every venue.',
    description: 'Deploy QR codes at hotels, restaurants, tourist spots, or event venues. Visitors scan for recommendations, directions, bookings, and local insights.',
    strategy: 'Deploy QR codes at hotels, restaurants, tourist spots, or event venues. Visitors scan for recommendations, directions, bookings, and local insights.',
    benefits: [
      'Enhanced guest experience',
      'Local recommendations 24/7',
      'Reduced front desk inquiries',
      'Increased venue bookings'
    ],
    targetAudience: ['Hotels', 'Restaurants', 'Tourist attractions', 'Event venues'],
    estimatedSetupTime: '20 minutes',
    difficulty: 'beginner',
    tags: ['hospitality', 'concierge', 'local-recommendations', 'venues'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Venue Concierge Playbook - QR Code Hospitality Genie',
    metaDescription: 'Transform guest experience with QR code venue Genies. Local recommendations, bookings, and directions at every location.',
    keywords: ['venue concierge', 'hotel Genie', 'local recommendations', 'hospitality Genie', 'guest experience']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "🏨 Welcome to the Grand Plaza Hotel! I'm your digital concierge. I can help with restaurant recommendations, arrange transportation, suggest local attractions, or answer questions about hotel amenities. How can I enhance your stay?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Welcome to Grand Plaza! I'm your digital concierge. How can I help?" },
    { speaker: 'user', message: "I need a good Italian restaurant nearby" },
    { speaker: 'agent', message: "Luigi's is excellent and just 5 minutes away. They're known for authentic pasta. Should I make a reservation?" },
    { speaker: 'user', message: "Yes, for 7pm tonight, 2 people" },
    { speaker: 'agent', message: "Perfect! I've confirmed your table at Luigi's for 7pm. I can send walking directions to your phone." }
  ],
  templateConfig: {
    firstMessage: "🏨 Welcome to [VENUE]! I'm your digital concierge. How can I help?",
    systemPrompt: "You are a location-based concierge Genie accessed via QR code at a physical venue. Provide local recommendations, help with bookings, give directions, and share insider tips. Be knowledgeable about the area and helpful.",
    tools: ['local_database', 'reservation_system', 'maps_integration', 'event_calendar', 'transportation_booking']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Guest scans venue QR code',
      description: 'Visitor scans QR code at hotel lobby, restaurant table, or attraction entrance'
    },
    {
      type: 'genie',
      icon: MapPin,
      genieIcon: 'genie-lamp',
      label: 'Provides location context',
      description: 'Genie knows exact venue and can provide specific amenities and local information'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'target',
      label: 'Offers personalized recommendations',
      description: 'Suggests restaurants, attractions, events based on preferences and proximity'
    },
    {
      type: 'action',
      icon: Utensils,
      genieIcon: 'calendar-check',
      label: 'Handles reservations',
      description: 'Books tables, tickets, or services directly through conversation interface'
    },
    {
      type: 'action',
      icon: Navigation,
      genieIcon: 'share',
      label: 'Provides directions & logistics',
      description: 'Shares walking directions, transportation options, and timing information'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Enhanced guest experience',
      description: 'Visitors enjoy personalized local expertise and seamless booking assistance'
    }
  ],
  metrics: {
    conversionRate: '43%',
    responseTime: '<1 second',
    satisfactionScore: '4.8/5',
    costSavings: '$30k/month',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'Grand Plaza Hotels',
      authorName: 'Maria Rodriguez',
      authorTitle: 'Guest Experience Manager',
      quote: 'Digital concierge reduced front desk inquiries by 40% while increasing guest satisfaction scores to record highs.',
      metrics: '40% reduction in front desk inquiries'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Local Recommendations Database',
      description: 'Structured format for organizing venue-specific local information',
      url: '/templates/local-recommendations'
    },
    {
      type: 'guide',
      title: 'Venue Integration Setup',
      description: 'How to connect with reservation systems and local service providers',
      url: '/docs/venue-integrations'
    }
  ]
};